import { Injectable } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { UploadObsService } from "./upload-obs.service";

@Injectable({
  providedIn: "root",
})
export class VideoThumbnailService {
  private readonly THUMBNAIL_MAX_DIMENSION = 640;
  private readonly THUMBNAIL_JPEG_QUALITY = 0.78;

  constructor(private uploadService: UploadObsService) {}

  private isCanvasSecurityError(error: any): boolean {
    if (!error) {
      return false;
    }
    const name = String((error && error.name) || "").toLowerCase();
    const message = String((error && error.message) || "").toLowerCase();
    return (
      name.includes("securityerror") ||
      message.includes("tainted") ||
      message.includes("cross-origin") ||
      message.includes("cross origin")
    );
  }

  private getThumbnailCanvasSize(videoWidth: number, videoHeight: number): { width: number; height: number } {
    const safeWidth = Math.max(1, videoWidth || 640);
    const safeHeight = Math.max(1, videoHeight || 360);
    const maxSide = Math.max(safeWidth, safeHeight);
    if (maxSide <= this.THUMBNAIL_MAX_DIMENSION) {
      return { width: safeWidth, height: safeHeight };
    }
    const scale = this.THUMBNAIL_MAX_DIMENSION / maxSide;
    return {
      width: Math.max(1, Math.round(safeWidth * scale)),
      height: Math.max(1, Math.round(safeHeight * scale)),
    };
  }

  private getCanvasSampleStats(
    canvas: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D
  ): { avgR: number; avgG: number; avgB: number; isBlack: boolean; isMostlyWhite: boolean; variance: number } {
    const w = Math.max(1, canvas.width || 1);
    const h = Math.max(1, canvas.height || 1);
    const imageData = ctx.getImageData(0, 0, w, h).data;

    const stepX = Math.max(1, Math.floor(w / 32));
    const stepY = Math.max(1, Math.floor(h / 32));
    const samples: Array<{ r: number; g: number; b: number }> = [];

    let sumR = 0;
    let sumG = 0;
    let sumB = 0;
    let blackLike = 0;
    let whiteLike = 0;

    for (let y = 0; y < h; y += stepY) {
      for (let x = 0; x < w; x += stepX) {
        const idx = (y * w + x) * 4;
        const r = imageData[idx];
        const g = imageData[idx + 1];
        const b = imageData[idx + 2];
        samples.push({ r, g, b });
        sumR += r;
        sumG += g;
        sumB += b;
        if (r <= 10 && g <= 10 && b <= 10) {
          blackLike++;
        }
        if (r >= 245 && g >= 245 && b >= 245) {
          whiteLike++;
        }
      }
    }

    const count = Math.max(1, samples.length);
    const avgR = sumR / count;
    const avgG = sumG / count;
    const avgB = sumB / count;

    let varianceSum = 0;
    for (const px of samples) {
      const dr = px.r - avgR;
      const dg = px.g - avgG;
      const db = px.b - avgB;
      varianceSum += dr * dr + dg * dg + db * db;
    }
    const variance = varianceSum / count;

    return {
      avgR,
      avgG,
      avgB,
      isBlack: blackLike / count > 0.98,
      isMostlyWhite: whiteLike / count > 0.92,
      variance,
    };
  }

  private isCanvasInvalidFrame(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D): boolean {
    if (!canvas || canvas.width < 2 || canvas.height < 2) {
      return true;
    }
    const stats = this.getCanvasSampleStats(canvas, ctx);
    const isHighLuma = stats.avgR > 248 && stats.avgG > 248 && stats.avgB > 248;
    // Only treat flat bright frames as invalid. Dark/flat scenes can be real content.
    const isNearlyUniformBright = stats.variance < 120 && stats.avgR > 228 && stats.avgG > 228 && stats.avgB > 228;
    // Reject only near-uniform dark frame (often decoder preroll black frame), not normal dark scenes.
    const isNearlyUniformDark =
      stats.variance < 35 && stats.avgR < 14 && stats.avgG < 14 && stats.avgB < 14 && stats.isBlack;
    return stats.isMostlyWhite || isHighLuma || isNearlyUniformBright || isNearlyUniformDark;
  }

  /**
   * Generate thumbnail from video file and upload to S3
   * @param file Video file to generate thumbnail from
   * @param s3Path S3 path for upload (optional, defaults to "digital/thumb")
   * @param maxSizeMB Maximum file size in MB (optional)
   * @returns Observable with S3 upload progress and final S3 URL
   */
  generateThumbnailFromVideo(file: File, s3Path: string = "digital/thumb", maxSizeMB: number = 5): Observable<any> {
    const subject = new Subject<any>();

    this.generateThumbnail(file)
      .then(thumbnailDataURL => {
        // Emit fast local preview first so UI can show poster immediately.
        subject.next({
          type: "preview",
          url: thumbnailDataURL,
          s3Url: null,
          message: "Local thumbnail ready",
        });
        const thumbnailBlob = this.dataURLtoBlob(thumbnailDataURL);
        if (thumbnailBlob) {
          // Generate unique filename with timestamp
          const timestamp = Date.now();
          const filename = `thumbnail_${timestamp}.jpg`;
          const thumbnailFile = new File([thumbnailBlob], filename, {
            type: "image/jpeg",
          });

          // Upload thumbnail to S3
          this.uploadService.uploadFileToS3(s3Path, thumbnailFile, maxSizeMB).subscribe(
            (response: any) => {
              if (response && response.type === "progress") {
                // Forward progress events
                subject.next({
                  type: "progress",
                  percent: response.percent,
                  message: "Uploading thumbnail...",
                });
              } else if (response && response.type === "complete") {
                // Upload completed successfully - return S3 URL
                console.log("Thumbnail uploaded to S3:", response.url);
                subject.next({
                  type: "complete",
                  url: response.url,
                  s3Url: response.url,
                  fileName: filename,
                  size: thumbnailFile.size,
                });
                subject.complete();
              }
            },
            error => {
              console.error("S3 upload failed, falling back to data URL:", error);
              // Fallback to data URL if S3 upload fails
              subject.next({
                type: "complete",
                url: thumbnailDataURL,
                s3Url: null,
                fileName: filename,
                size: thumbnailFile.size,
                error: "S3 upload failed",
              });
              subject.complete();
            }
          );
        } else {
          subject.error(new Error("Failed to convert thumbnail to blob"));
        }
      })
      .catch(err => {
        console.error("Thumbnail generation failed:", err);
        subject.error(err);
      });

    return subject.asObservable();
  }

  /**
   * Generate thumbnail from uploaded video URL and upload to S3.
   * Used as a fallback when local file-based extraction yields blank/invalid preview.
   */
  generateThumbnailFromVideoUrl(
    videoUrl: string,
    s3Path: string = "digital/thumb",
    maxSizeMB: number = 5
  ): Observable<any> {
    const subject = new Subject<any>();

    this.generateThumbnailFromUrl(videoUrl)
      .then(thumbnailDataURL => {
        // Emit fast local preview first so UI can show poster immediately.
        subject.next({
          type: "preview",
          url: thumbnailDataURL,
          s3Url: null,
          message: "Local thumbnail ready",
        });
        const thumbnailBlob = this.dataURLtoBlob(thumbnailDataURL);
        if (thumbnailBlob) {
          const timestamp = Date.now();
          const filename = `thumbnail_${timestamp}.jpg`;
          const thumbnailFile = new File([thumbnailBlob], filename, {
            type: "image/jpeg",
          });

          this.uploadService.uploadFileToS3(s3Path, thumbnailFile, maxSizeMB).subscribe(
            (response: any) => {
              if (response && response.type === "progress") {
                subject.next({
                  type: "progress",
                  percent: response.percent,
                  message: "Uploading thumbnail...",
                });
              } else if (response && response.type === "complete") {
                subject.next({
                  type: "complete",
                  url: response.url,
                  s3Url: response.url,
                  fileName: filename,
                  size: thumbnailFile.size,
                });
                subject.complete();
              }
            },
            error => {
              console.error("S3 upload failed (URL thumbnail), falling back to data URL:", error);
              subject.next({
                type: "complete",
                url: thumbnailDataURL,
                s3Url: null,
                fileName: filename,
                size: thumbnailFile.size,
                error: "S3 upload failed",
              });
              subject.complete();
            }
          );
        } else {
          subject.error(new Error("Failed to convert URL thumbnail to blob"));
        }
      })
      .catch(err => {
        console.error("Thumbnail generation from URL failed:", err);
        if (this.isCanvasSecurityError(err)) {
          subject.error(
            new Error(
              "Cannot generate thumbnail from this video URL because browser canvas is blocked by CORS. Please enable CORS for the video host (S3/CloudFront) and retry."
            )
          );
          return;
        }
        subject.error(err);
      });

    return subject.asObservable();
  }

  /**
   * Generate thumbnail from video file using canvas
   * @param file Video file
   * @returns Promise<string> Base64 data URL of the thumbnail
   */
  private generateThumbnail(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      try {
        if (!file || !file.type.startsWith("video/")) {
          return reject(new Error("Invalid video file"));
        }

        const video = document.createElement("video");
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Could not get canvas context"));

        video.muted = true;
        video.preload = "metadata";
        // Blob/object URLs are same-origin; avoid crossOrigin here — it can break decode/canvas on some browsers.
        video.setAttribute("playsinline", "");

        const objectURL = URL.createObjectURL(file);
        let cleaned = false;
        let attemptCount = 0;
        const maxAttempts = 8;
        let failTimer: any = null;

        const cleanup = () => {
          if (cleaned) return;
          cleaned = true;
          if (failTimer != null) {
            clearTimeout(failTimer);
            failTimer = null;
          }
          URL.revokeObjectURL(objectURL);
        };

        const drawVideoFrameToCanvas = (): void => {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        };

        const scheduleDrawAfterDecodedFrame = (draw: () => void): void => {
          const anyVideo = video as HTMLVideoElement & {
            requestVideoFrameCallback?: (cb: () => void) => number;
          };
          if (typeof anyVideo.requestVideoFrameCallback === "function") {
            anyVideo.requestVideoFrameCallback(() => draw());
          } else {
            requestAnimationFrame(() => draw());
          }
        };

        const scheduleRetry = (fn: () => void): void => {
          // Do not block on tab visibility; continue retries in background tabs.
          setTimeout(fn, 200);
        };

        const tryDrawFrame = (seekTime: number = 0): void => {
          attemptCount++;

          const runCapture = () => {
            void (async () => {
              const onSeekedDraw = () => {
                try {
                  if (video.readyState < 2) {
                    console.log(`Video not ready, readyState: ${video.readyState}`);
                    if (attemptCount < maxAttempts) {
                      scheduleRetry(() => tryDrawFrame(seekTime + 0.5));
                    }
                    return;
                  }

                  scheduleDrawAfterDecodedFrame(() => {
                    try {
                      drawVideoFrameToCanvas();

                      if (this.isCanvasInvalidFrame(canvas, ctx)) {
                        console.log(`Canvas invalid (blank/white/uniform), attempt ${attemptCount}/${maxAttempts}`);
                        if (attemptCount < maxAttempts) {
                          const newSeekTime = Math.min(
                            seekTime + (attemptCount < 4 ? 1 : 2),
                            Math.max(0, (video.duration || 10) - 0.1)
                          );
                          scheduleRetry(() => tryDrawFrame(newSeekTime));
                          return;
                        }
                        cleanup();
                        reject(
                          new Error(
                            "Could not capture a valid video thumbnail. Keep this browser tab visible while thumbnails are generated."
                          )
                        );
                        return;
                      }

                      const dataURL = canvas.toDataURL("image/jpeg", this.THUMBNAIL_JPEG_QUALITY);

                      if (!dataURL || dataURL === "data:,") {
                        throw new Error("Generated dataURL is invalid");
                      }

                      console.log(`Thumbnail generated successfully on attempt ${attemptCount}`);
                      cleanup();
                      resolve(dataURL);
                    } catch (e) {
                      console.error(`Error in attempt ${attemptCount}:`, e);
                      if (attemptCount < maxAttempts) {
                        scheduleRetry(() => tryDrawFrame(seekTime + 0.5));
                      } else {
                        cleanup();
                        reject(e as Error);
                      }
                    }
                  });
                } catch (e) {
                  console.error(`Error in attempt ${attemptCount}:`, e);
                  if (attemptCount < maxAttempts) {
                    scheduleRetry(() => tryDrawFrame(seekTime + 0.5));
                  } else {
                    cleanup();
                    reject(e as Error);
                  }
                }
              };

              video.addEventListener("seeked", onSeekedDraw, { once: true });

              try {
                const dur = video.duration;
                if (dur && !Number.isNaN(dur) && seekTime < dur) {
                  video.currentTime = seekTime;
                } else {
                  video.removeEventListener("seeked", onSeekedDraw);
                  onSeekedDraw();
                }
              } catch (seekError) {
                console.log("Seek failed, trying current frame:", seekError);
                video.removeEventListener("seeked", onSeekedDraw);
                onSeekedDraw();
              }
            })();
          };

          runCapture();
        };

        video.addEventListener("error", () => {
          const mediaErr = (video.error && video.error.code) || 0;
          let msg = "Video loading error.";
          if (mediaErr === 4) msg = "Video source not supported (format/codec issue).";
          cleanup();
          reject(new Error(`${msg} type=${file.type}; code=${mediaErr}`));
        });

        video.addEventListener(
          "loadedmetadata",
          () => {
            const { width, height } = this.getThumbnailCanvasSize(video.videoWidth || 640, video.videoHeight || 360);
            canvas.width = width;
            canvas.height = height;

            console.log(`Video metadata loaded: ${canvas.width}x${canvas.height}, duration: ${video.duration}`);

            const seekTime = Math.min(3, Math.max(1, (video.duration || 10) * 0.2));

            setTimeout(() => {
              tryDrawFrame(seekTime);
            }, 100);
          },
          { once: true }
        );

        failTimer = setTimeout(() => {
          if (!cleaned) {
            console.log("Thumbnail generation timeout");
            cleanup();
            reject(new Error("Thumbnail generation timeout after 45 seconds"));
          }
        }, 45000);

        video.src = objectURL;
      } catch (err) {
        reject(err as Error);
      }
    });
  }

  /**
   * Generate thumbnail from a remote video URL.
   */
  private generateThumbnailFromUrl(videoUrl: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!videoUrl || typeof videoUrl !== "string") {
        reject(new Error("Invalid video URL"));
        return;
      }

      const video = document.createElement("video");
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Could not get canvas context"));
        return;
      }

      let finished = false;
      let attemptCount = 0;
      const maxAttempts = 8;
      let playbackFallbackTried = false;
      const finish = (cb: () => void) => {
        if (finished) return;
        finished = true;
        cb();
      };

      const timeoutId = setTimeout(() => {
        finish(() => reject(new Error("Thumbnail generation from URL timeout after 20 seconds")));
      }, 20000);

      const cleanup = () => {
        clearTimeout(timeoutId);
        try {
          video.pause();
        } catch (_e) {
          // no-op
        }
        video.removeAttribute("src");
        try {
          video.load();
        } catch (e) {
          // no-op
        }
      };

      video.muted = true;
      video.preload = "auto";
      video.crossOrigin = "anonymous";
      video.setAttribute("playsinline", "");

      const scheduleRetry = (fn: () => void): void => {
        // Do not block on tab visibility; continue retries in background tabs.
        setTimeout(fn, 200);
      };

      const tryPlaybackFallbackCapture = (failMessage: string): void => {
        if (playbackFallbackTried) {
          finish(() => {
            cleanup();
            reject(new Error(failMessage));
          });
          return;
        }

        playbackFallbackTried = true;

        const captureFromCurrentFrame = () => {
          if (finished) {
            return;
          }
          try {
            if (video.currentTime <= 0 && video.duration && !Number.isNaN(video.duration)) {
              video.currentTime = Math.min(1, Math.max(0, video.duration - 0.1));
            }
          } catch (_e) {
            // keep current frame if seeking fails
          }

          setTimeout(() => {
            if (finished) {
              return;
            }
            try {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              if (this.isCanvasInvalidFrame(canvas, ctx)) {
                finish(() => {
                  cleanup();
                  reject(new Error(failMessage));
                });
                return;
              }

              const dataURL = canvas.toDataURL("image/jpeg", this.THUMBNAIL_JPEG_QUALITY);
              if (!dataURL || dataURL === "data:,") {
                finish(() => {
                  cleanup();
                  reject(new Error("Generated dataURL is invalid after playback fallback"));
                });
                return;
              }

              finish(() => {
                cleanup();
                resolve(dataURL);
              });
            } catch (e) {
              if (this.isCanvasSecurityError(e)) {
                finish(() => {
                  cleanup();
                  reject(
                    new Error(
                      "Canvas access blocked by CORS while reading video URL. Configure Access-Control-Allow-Origin on video files."
                    )
                  );
                });
                return;
              }

              finish(() => {
                cleanup();
                reject(new Error(failMessage));
              });
            }
          }, 350);
        };

        try {
          const playResult = video.play();
          if (playResult && typeof (playResult as Promise<void>).then === "function") {
            (playResult as Promise<void>).then(() => captureFromCurrentFrame()).catch(() => captureFromCurrentFrame());
          } else {
            captureFromCurrentFrame();
          }
        } catch (_e) {
          captureFromCurrentFrame();
        }
      };

      const tryCaptureAt = (targetTime: number): void => {
        attemptCount++;
        const runCapture = () => {
          const onSeeked = () => {
            try {
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              if (this.isCanvasInvalidFrame(canvas, ctx)) {
                if (attemptCount < maxAttempts) {
                  const nextTime = Math.min(
                    targetTime + (attemptCount < 4 ? 1 : 2),
                    Math.max(0, (video.duration || 10) - 0.1)
                  );
                  scheduleRetry(() => tryCaptureAt(nextTime));
                  return;
                }
                tryPlaybackFallbackCapture(
                  "Could not capture a valid video thumbnail from URL. Keep this tab visible."
                );
                return;
              }

              const dataURL = canvas.toDataURL("image/jpeg", this.THUMBNAIL_JPEG_QUALITY);
              if (!dataURL || dataURL === "data:,") {
                if (attemptCount < maxAttempts) {
                  const nextTime = Math.min(targetTime + 0.5, Math.max(0, (video.duration || 10) - 0.1));
                  scheduleRetry(() => tryCaptureAt(nextTime));
                  return;
                }
                tryPlaybackFallbackCapture("Generated dataURL is invalid");
                return;
              }
              finish(() => {
                cleanup();
                resolve(dataURL);
              });
            } catch (e) {
              if (this.isCanvasSecurityError(e)) {
                finish(() => {
                  cleanup();
                  reject(
                    new Error(
                      "Canvas access blocked by CORS while reading video URL. Configure Access-Control-Allow-Origin on video files."
                    )
                  );
                });
                return;
              }
              if (attemptCount < maxAttempts) {
                const nextTime = Math.min(targetTime + 0.5, Math.max(0, (video.duration || 10) - 0.1));
                scheduleRetry(() => tryCaptureAt(nextTime));
                return;
              }
              tryPlaybackFallbackCapture((e as Error).message || "Failed to capture thumbnail from video URL");
            }
          };

          video.addEventListener("seeked", onSeeked, { once: true });
          try {
            if (video.duration && !Number.isNaN(video.duration) && targetTime < video.duration) {
              video.currentTime = targetTime;
            } else {
              video.removeEventListener("seeked", onSeeked);
              onSeeked();
            }
          } catch (_e) {
            video.removeEventListener("seeked", onSeeked);
            onSeeked();
          }
        };

        runCapture();
      };

      video.addEventListener(
        "error",
        () => {
          const mediaErr = (video.error && video.error.code) || 0;
          finish(() => {
            cleanup();
            reject(new Error(`Video URL loading error. code=${mediaErr}`));
          });
        },
        { once: true }
      );

      video.addEventListener(
        "loadedmetadata",
        () => {
          const { width, height } = this.getThumbnailCanvasSize(video.videoWidth || 640, video.videoHeight || 360);
          canvas.width = width;
          canvas.height = height;
          const targetTime = Math.min(3, Math.max(1, (video.duration || 10) * 0.2));
          tryCaptureAt(targetTime);
        },
        { once: true }
      );

      video.src = videoUrl;
    });
  }

  /**
   * Convert base64 data URL to Blob
   * @param dataURL Base64 data URL
   * @returns Blob or null
   */
  private dataURLtoBlob(dataURL: string): Blob | null {
    try {
      const arr = dataURL.split(",");
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) return null;

      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);

      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }

      return new Blob([u8arr], { type: mime });
    } catch (error) {
      console.error("Error converting data URL to blob:", error);
      return null;
    }
  }
}
